/**
 * Tiny dependency-free Markdown → HTML renderer for read-only previews.
 *
 * v0.1 deliberately avoids a markdown library (spec §18: "Markdown textarea +
 * preview"). Input is HTML-escaped FIRST, so user content can never inject
 * markup; only a small, safe subset of inline/block syntax is then re-expanded.
 *
 * Supported: ATX headings (#..######), unordered (-,*,+) and ordered lists,
 * fenced ``` code blocks, inline `code`, **bold**, *italic*, [text](url)
 * links (http/https/relative only), blockquotes, horizontal rules, and
 * paragraphs separated by blank lines.
 */

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim();
  if (/^(https?:)?\/\//i.test(trimmed)) return true;
  // Allow relative and anchor links; reject javascript:, data:, etc.
  return /^[./#?]/.test(trimmed) || /^[a-z0-9._~-]/i.test(trimmed)
    ? !/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || /^https?:/i.test(trimmed)
    : false;
}

/** Inline formatting on already-escaped text. */
function renderInline(escaped: string): string {
  let out = escaped;
  // inline code first so its contents are not further transformed
  const codeStore: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, code: string) => {
    codeStore.push(code);
    return ` CODE${codeStore.length - 1} `;
  });

  // links [text](url)
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (match, text: string, url: string) => {
      if (!isSafeUrl(url)) return match;
      return `<a href="${url}" target="_blank" rel="noreferrer noopener" class="text-primary underline underline-offset-2">${text}</a>`;
    },
  );

  // bold then italic
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");

  // restore inline code
  out = out.replace(/ CODE(\d+) /g, (_m, i: string) => {
    const code = codeStore[Number(i)] ?? "";
    return `<code class="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">${code}</code>`;
  });

  return out;
}

export function renderMarkdown(source: string): string {
  const escaped = escapeHtml(source ?? "");
  const lines = escaped.split(/\r?\n/);
  const html: string[] = [];

  let i = 0;
  let paragraph: string[] = [];
  let listType: "ul" | "ol" | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      html.push(`<p>${renderInline(paragraph.join(" "))}</p>`);
      paragraph = [];
    }
  };
  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // fenced code block
    if (/^```/.test(line.trim())) {
      flushParagraph();
      closeList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test((lines[i] ?? "").trim())) {
        code.push(lines[i] ?? "");
        i++;
      }
      i++; // skip closing fence
      html.push(
        `<pre class="overflow-x-auto rounded-md bg-muted p-3 text-sm"><code class="font-mono">${code.join(
          "\n",
        )}</code></pre>`,
      );
      continue;
    }

    // blank line
    if (line.trim() === "") {
      flushParagraph();
      closeList();
      i++;
      continue;
    }

    // horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushParagraph();
      closeList();
      html.push('<hr class="my-4 border-border" />');
      i++;
      continue;
    }

    // heading
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      closeList();
      const level = (heading[1] ?? "").length;
      const sizes = [
        "text-2xl",
        "text-xl",
        "text-lg",
        "text-base",
        "text-sm",
        "text-sm",
      ];
      const size = sizes[level - 1] ?? "text-sm";
      html.push(
        `<h${level} class="mt-3 mb-1 font-semibold ${size}">${renderInline(
          heading[2] ?? "",
        )}</h${level}>`,
      );
      i++;
      continue;
    }

    // blockquote
    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      flushParagraph();
      closeList();
      html.push(
        `<blockquote class="border-l-2 border-border pl-3 italic text-muted-foreground">${renderInline(
          quote[1] ?? "",
        )}</blockquote>`,
      );
      i++;
      continue;
    }

    // unordered list item
    const ul = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (ul) {
      flushParagraph();
      if (listType !== "ul") {
        closeList();
        html.push('<ul class="ml-5 list-disc space-y-1">');
        listType = "ul";
      }
      html.push(`<li>${renderInline(ul[1] ?? "")}</li>`);
      i++;
      continue;
    }

    // ordered list item
    const ol = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (ol) {
      flushParagraph();
      if (listType !== "ol") {
        closeList();
        html.push('<ol class="ml-5 list-decimal space-y-1">');
        listType = "ol";
      }
      html.push(`<li>${renderInline(ol[1] ?? "")}</li>`);
      i++;
      continue;
    }

    // plain paragraph line
    closeList();
    paragraph.push(line.trim());
    i++;
  }

  flushParagraph();
  closeList();

  return html.join("\n");
}
